import { useState, type ReactNode } from "react";
import { formatMessageTime } from "../lib/format";
import { useLocale, useT } from "../lib/useT";
import type { MessageKey } from "../lib/i18n";

/** Startup phases the Gateway names, each with a line of its own. */
const PHASES: Record<string, true> = {
  "phase.queued": true,
  "phase.waiting_for_slot": true,
  "phase.provisioning_environment": true,
  "phase.preparing_context": true,
  "phase.starting_model": true,
};
import type { ChatEntry, ToolActivity } from "../openclaw/transcript";
import { BubbleAttachments } from "./Attachments";

/** Only http(s) survives; anything else (javascript:, data:) renders as text. */
function safeHref(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * Renders the small markdown subset agent replies actually use.
 *
 * A messenger reads best as near-plain text, so headings, tables and the rest
 * stay literal. But code, emphasis and links appear in almost every answer, and
 * leaving those as raw `**asterisks**` looks worse than either extreme. Output
 * is assembled from split strings and never from HTML, so model text cannot
 * inject markup; link targets are additionally restricted to http(s).
 */
function renderText(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const fence = /```([a-zA-Z0-9+#._-]*)\n?([\s\S]*?)```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  // Code spans are matched first so their contents are never re-interpreted.
  const INLINE =
    /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(__[^_\n]+__)|(\[[^\]\n]+\]\([^)\s]+\))|(\*[^*\n]+\*)/g;

  const pushInline = (value: string) => {
    if (!value) {
      return;
    }
    let cursor = 0;
    let token: RegExpExecArray | null;
    INLINE.lastIndex = 0;
    while ((token = INLINE.exec(value)) !== null) {
      if (token.index > cursor) {
        nodes.push(<span key={`k${key++}`}>{value.slice(cursor, token.index)}</span>);
      }
      const [raw, code, bold, boldAlt, link, italic] = token;
      if (code) {
        nodes.push(<code key={`k${key++}`}>{code.slice(1, -1)}</code>);
      } else if (bold || boldAlt) {
        nodes.push(<strong key={`k${key++}`}>{(bold ?? boldAlt).slice(2, -2)}</strong>);
      } else if (link) {
        const split = link.indexOf("](");
        const label = link.slice(1, split);
        const href = safeHref(link.slice(split + 2, -1));
        nodes.push(
          href ? (
            <a key={`k${key++}`} href={href} target="_blank" rel="noreferrer noopener">
              {label}
            </a>
          ) : (
            <span key={`k${key++}`}>{link}</span>
          ),
        );
      } else if (italic) {
        nodes.push(<em key={`k${key++}`}>{italic.slice(1, -1)}</em>);
      } else {
        nodes.push(<span key={`k${key++}`}>{raw}</span>);
      }
      cursor = token.index + raw.length;
    }
    if (cursor < value.length) {
      nodes.push(<span key={`k${key++}`}>{value.slice(cursor)}</span>);
    }
  };

  while ((match = fence.exec(text)) !== null) {
    pushInline(text.slice(lastIndex, match.index));
    nodes.push(<pre key={`k${key++}`}>{match[2].replace(/\n$/, "")}</pre>);
    lastIndex = match.index + match[0].length;
  }
  pushInline(text.slice(lastIndex));
  return nodes;
}

export function ToolChip({ activity }: { activity: ToolActivity }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const { presentation } = activity;
  const hasOutput = Boolean(activity.result?.trim());

  return (
    <>
      <button
        type="button"
        className={`tool-chip${activity.isError ? " is-error" : ""}`}
        onClick={() => hasOutput && setOpen((value) => !value)}
        aria-expanded={hasOutput ? open : undefined}
        disabled={!hasOutput}
      >
        <span className="tool-chip-emoji" aria-hidden="true">
          {presentation.emoji}
        </span>
        <span className="tool-chip-body">
          <span className="tool-chip-title">{presentation.title}</span>
          {presentation.detail ? (
            <span className="tool-chip-detail">{presentation.detail}</span>
          ) : null}
        </span>
        {!activity.completed ? (
          <span className="tool-chip-status">{t("bubble.running")}</span>
        ) : activity.isError ? (
          <span className="tool-chip-status">{t("bubble.error")}</span>
        ) : hasOutput ? (
          <span className="tool-chip-status">{open ? t("bubble.less") : t("bubble.more")}</span>
        ) : null}
      </button>
      {open && hasOutput ? (
        <div className="tool-output">{activity.result?.slice(0, 8_000)}</div>
      ) : null}
    </>
  );
}

export function MessageBubble({
  entry,
  sessionKey,
}: {
  entry: ChatEntry;
  sessionKey?: string;
}) {
  const t = useT();
  const locale = useLocale();
  if (entry.role === "system") {
    return (
      <div className="bubble-row is-system">
        <div className="system-note">{entry.text}</div>
      </div>
    );
  }

  const isOut = entry.role === "user";
  const classes = [
    "bubble-row",
    isOut ? "is-out" : "is-in",
    entry.pending ? "is-pending" : "",
    entry.failed ? "is-failed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const mediaOnly = !entry.text && entry.attachments.length > 0;

  return (
    <div className={classes}>
      <div className={`bubble${mediaOnly ? " is-media" : ""}`}>
        <BubbleAttachments attachments={entry.attachments} sessionKey={sessionKey} />
        {entry.toolActivities.length ? (
          <div className="tool-list">
            {entry.toolActivities.map((activity) => (
              <ToolChip key={activity.id} activity={activity} />
            ))}
          </div>
        ) : null}
        {entry.text ? <div className="bubble-text">{renderText(entry.text)}</div> : null}
        <div className="bubble-meta">
          {entry.failed ? <span>{t("bubble.notSent")}</span> : null}
          {entry.pending ? <span>{t("bubble.sending")}</span> : null}
          <span>{formatMessageTime(entry.timestamp, locale)}</span>
        </div>
      </div>
    </div>
  );
}

/** The provisional bubble that shows a run while it is still streaming. */
export function StreamingBubble({
  text,
  phase,
  errorMessage,
  failed,
}: {
  text: string;
  phase?: string;
  errorMessage?: string;
  failed?: boolean;
}) {
  const t = useT();
  const phaseKey = phase ? (`phase.${phase}` as MessageKey) : undefined;
  const label = phaseKey && phaseKey in PHASES ? t(phaseKey) : undefined;

  return (
    <div className={`bubble-row is-in${failed ? " is-failed" : ""}`}>
      <div className="bubble">
        {text ? (
          <div className="bubble-text">
            {renderText(text)}
            {!errorMessage ? <span className="cursor-caret" /> : null}
          </div>
        ) : (
          <div className="working-line">
            <span className="typing" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            <span>
              {label ? t("bubble.workingPhase", { phase: label }) : t("bubble.workingPlain")}
            </span>
          </div>
        )}
        {errorMessage ? <div className="bubble-meta">{errorMessage}</div> : null}
      </div>
    </div>
  );
}
